import { PageLoader, useMinLoading } from "@/components/Preloader";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Rss, UserPlus } from "lucide-react";
import api from "@/lib/api";
import { Article } from "@/lib/types";
import { useAuth } from "@/context/AuthContext";
import { ArticleCard } from "@/components/ArticleCard";

export default function MyFeed() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["my-feed"],
    queryFn: async () => (await api.get("/account/my-feed")).data,
    enabled: !!user,
  });
  const showLoader = useMinLoading(isLoading);

  if (loading) return <div className="py-24 text-center text-gray-500">Loading…</div>;
  if (!user) {
    navigate("/login");
    return null;
  }

  const items: Article[] = data?.items || [];
  const authors: { name: string; slug: string }[] = data?.followed_authors || [];

  return (
    <div data-testid="my-feed-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-3">
        <Rss className="h-4 w-4" /> My Feed
      </div>
      <h1 className="font-heading font-black text-white text-3xl sm:text-4xl">Stories from authors you follow</h1>

      {authors.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-5">
          {authors.map((a) => (
            <Link key={a.slug} to={`/author/${a.slug}`} data-testid={`followed-author-chip-${a.slug}`}
              className="font-mono text-[11px] uppercase tracking-widest text-[#7C3AED] bg-[#7C3AED]/10 border border-[#7C3AED]/25 px-3 py-1 rounded hover:bg-[#7C3AED]/20 transition-colors">
              {a.name}
            </Link>
          ))}
        </div>
      )}

      {showLoader ? (
        <PageLoader />
      ) : items.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {items.map((a) => <ArticleCard key={a.id} article={a} />)}
        </div>
      ) : (
        <div data-testid="my-feed-empty" className="mt-12 text-center py-20 border border-dashed border-white/10 rounded-xl">
          <UserPlus className="h-10 w-10 text-gray-600 mx-auto mb-4" />
          <h2 className="font-heading font-bold text-white text-xl">You're not following any authors yet</h2>
          <p className="text-gray-400 mt-2 max-w-md mx-auto">
            Open any story and tap the author's name, then hit <span className="text-white font-semibold">Follow</span> —
            their new stories will land here.
          </p>
          <Link to="/latest" className="inline-block mt-6 bg-[#00E5FF] text-black font-bold px-6 py-2.5 rounded-md hover:bg-[#00c9e0] transition-colors">
            Browse the latest stories
          </Link>
        </div>
      )}
    </div>
  );
}
